import { create } from 'zustand';
import { useInventoryStore, NewStockUnit, LogEntry, formatReceiptSerialMac } from './inventoryStore';

// ── Pending action types ───────────────────────────────────────────────────

export interface AddStockUnitIdent {
  serial: string;
  mac: string;
}

export interface AddStockRow {
  itemId: string;
  quantity: number;
  condition: 'new' | 'used';
  /** One entry per unit */
  units: AddStockUnitIdent[];
  reference: string;
  owner: string;
}

export interface PendingAddStock {
  type: 'add_stock';
  id: string;
  rows: AddStockRow[];
  warehouseId: string;
  storageAreaId: string | null;
}

export interface PendingReturn {
  type: 'return_stock';
  id: string;
  unitIds: string[];
  warehouseId: string;
  storageAreaId: string | null;
  condition: 'new' | 'used';
  reference: string;
  notes: string;
}

export interface PendingTransfer {
  type: 'transfer';
  id: string;
  unitIds: string[];
  toWarehouseId: string;
  toStorageAreaId: string | null;
  reference: string;
}

export interface PendingCheckOut {
  type: 'check_out';
  id: string;
  unitIds: string[];
  owner: string;
  reference: string;
  notes: string;
}

export type PendingAction = PendingAddStock | PendingReturn | PendingTransfer | PendingCheckOut;

export type CommitResult = {
  success: boolean;
  message: string;
  counts: {
    added: number;
    returned: number;
    transferred: number;
    checkedOut: number;
  };
};

// ── Store ─────────────────────────────────────────────────────────────────

interface StockMovementState {
  pendingActions: PendingAction[];
  isCommitting: boolean;
  lastResult: CommitResult | null;

  addPending: (action: PendingAction) => void;
  removePending: (id: string) => void;
  clearAll: () => void;
  clearLastResult: () => void;
  commitAll: () => Promise<CommitResult>;
}

let pendingIdCounter = 0;
export const generatePendingId = () => `pending_${++pendingIdCounter}_${Date.now()}`;

export const useStockMovementStore = create<StockMovementState>((set, get) => ({
  pendingActions: [],
  isCommitting: false,
  lastResult: null,

  addPending: (action) =>
    set(s => ({ pendingActions: [...s.pendingActions, action] })),

  removePending: (id) =>
    set(s => ({ pendingActions: s.pendingActions.filter(a => a.id !== id) })),

  clearAll: () => set({ pendingActions: [], lastResult: null }),

  clearLastResult: () => set({ lastResult: null }),

  commitAll: async () => {
    const { pendingActions, isCommitting } = get();
    if (pendingActions.length === 0) {
      return { success: true, message: 'Nothing to commit.', counts: { added: 0, returned: 0, transferred: 0, checkedOut: 0 } };
    }
    if (isCommitting) {
      return { success: false, message: 'A commit is already in progress.', counts: { added: 0, returned: 0, transferred: 0, checkedOut: 0 } };
    }

    set({ isCommitting: true });

    const counts = { added: 0, returned: 0, transferred: 0, checkedOut: 0 };
    const errors: string[] = [];
    const logsBatch: LogEntry[] = [];
    let result: CommitResult = {
      success: false,
      message: 'Commit failed unexpectedly.',
      counts,
    };

    try {
      const inv = () => useInventoryStore.getState();

      // ── Step 1: Add Stock first (so new units exist before check-out/transfer) ──
      const addActions = pendingActions.filter((a): a is PendingAddStock => a.type === 'add_stock');
      for (const action of addActions) {
        const unitsToCreate: NewStockUnit[] = [];
        for (const row of action.rows) {
          const effectiveQty = Math.max(1, row.quantity);
          const item = inv().items.find(it => it.id === row.itemId);
          const requiresSerial = item?.requiresSerial ?? false;
          const requiresMac = item?.requiresMac ?? false;
          for (let i = 0; i < effectiveQty; i++) {
            const ident = row.units[i] ?? { serial: '', mac: '' };
            unitsToCreate.push({
              itemId: row.itemId,
              warehouseId: action.warehouseId,
              storageAreaId: action.storageAreaId,
              serialMac: formatReceiptSerialMac(ident.serial, ident.mac, requiresSerial, requiresMac),
              condition: row.condition,
              status: 'in stock',
              reference: row.reference || null,
              owner: row.owner || null,
              notes: null,
            });
          }
          counts.added += effectiveQty;
        }
        const created = await inv().addStockUnits(unitsToCreate);
        if (created.length !== unitsToCreate.length) {
          errors.push(`Add Stock: only ${created.length} of ${unitsToCreate.length} units were created.`);
        }
      }

      const freshUnits = inv().stockUnits;

      // ── Step 2: Return Stock ──
      const returnActions = pendingActions.filter((a): a is PendingReturn => a.type === 'return_stock');
      for (const action of returnActions) {
        for (const unitId of action.unitIds) {
          const unit = freshUnits.find(u => u.id === unitId);
          const description = `Returned to stock · ${action.condition}${action.reference ? ` · ref: ${action.reference}` : ''}`;
          const logOptions = {
            movementType: 'return_stock',
            fromWarehouseId: unit?.warehouseId ?? null,
            fromStorageAreaId: unit?.storageAreaId ?? null,
            toWarehouseId: action.warehouseId,
            toStorageAreaId: action.storageAreaId,
          };
          await inv().updateStockUnit(
            unitId,
            {
              status: 'in stock',
              warehouseId: action.warehouseId,
              storageAreaId: action.storageAreaId,
              condition: action.condition,
            },
            description,
            logOptions,
            { skipLog: true },
          );
          logsBatch.push({ stockUnitId: unitId, eventType: 'Edited', description, options: logOptions });
          counts.returned++;
        }
      }

      // ── Step 3: Transfers ──
      const transferActions = pendingActions.filter((a): a is PendingTransfer => a.type === 'transfer');
      for (const action of transferActions) {
        for (const unitId of action.unitIds) {
          const unit = freshUnits.find(u => u.id === unitId);
          const description = `Transferred${action.reference ? ` · ref: ${action.reference}` : ''}`;
          const logOptions = {
            movementType: 'transfer',
            fromWarehouseId: unit?.warehouseId ?? null,
            fromStorageAreaId: unit?.storageAreaId ?? null,
            toWarehouseId: action.toWarehouseId,
            toStorageAreaId: action.toStorageAreaId,
          };
          await inv().updateStockUnit(
            unitId,
            { warehouseId: action.toWarehouseId, storageAreaId: action.toStorageAreaId },
            description,
            logOptions,
            { skipLog: true },
          );
          logsBatch.push({ stockUnitId: unitId, eventType: 'Edited', description, options: logOptions });
          counts.transferred++;
        }
      }

      // ── Step 4: Check Out ──
      const checkOutActions = pendingActions.filter((a): a is PendingCheckOut => a.type === 'check_out');
      for (const action of checkOutActions) {
        for (const unitId of action.unitIds) {
          const unit = freshUnits.find(u => u.id === unitId);
          const description = `Checked out to ${action.owner || 'unknown'}${action.reference ? ` · ref: ${action.reference}` : ''}`;
          const logOptions = {
            movementType: 'check_out',
            fromWarehouseId: unit?.warehouseId ?? null,
            fromStorageAreaId: unit?.storageAreaId ?? null,
          };
          await inv().updateStockUnit(
            unitId,
            { status: 'checked out', owner: action.owner || null, reference: action.reference || null },
            description,
            logOptions,
            { skipLog: true },
          );
          logsBatch.push({ stockUnitId: unitId, eventType: 'Edited', description, options: logOptions });
          counts.checkedOut++;
        }
      }

      // One batched log insert for all movement updates.
      if (logsBatch.length > 0) {
        await inv().addLogsBatch(logsBatch);
      }

      result = {
        success: errors.length === 0,
        message: errors.length > 0 ? errors.join(' ') : 'All actions committed successfully.',
        counts,
      };
    } catch (err) {
      console.error('[commitAll] unexpected error:', err);
      const message = err instanceof Error ? err.message : 'Commit failed unexpectedly.';
      result = {
        success: false,
        message: errors.length > 0 ? `${errors.join(' ')} ${message}` : message,
        counts,
      };
    } finally {
      set({
        isCommitting: false,
        pendingActions: result.success ? [] : get().pendingActions,
        lastResult: result,
      });
      // Refresh in background — never block commit completion on a refetch.
      useInventoryStore.getState().fetchStockUnits().catch(() => {});
      useInventoryStore.getState().fetchRecentLogs().catch(() => {});
    }

    return result;
  },
}));
